/**
 * @file
 * The type-choice quick-create view (multi-bundle auto_create fields).
 */

((Drupal) => {
  const { berTags } = Drupal;
  const { components } = berTags;
  const {
    el,
    button,
    icon,
    iconButton,
    textSpan,
    setHidden,
    isHidden,
    onClick,
  } = berTags.dom;
  const { announce } = berTags.util;

  // A create view for fields allowing several bundles: a required type
  // select plus the title input. onSubmit(option, title) returns a promise;
  // busy/error handling lives here. onToggle(open) lets the host swap the
  // rest of the popover away while the view is open.
  components.createView = ({ label, options, onSubmit, onToggle }) => {
    const wrap = el('div', 'ber-tags__create');

    const toggle = button('ber-tags__create-toggle');
    toggle.append(
      icon('add'),
      textSpan('ber-tags__create-toggle-label', label),
    );

    const view = el('div', 'ber-tags__create-view is-hidden');

    const typeLabel = el('label', 'ber-tags__create-view-label');
    typeLabel.textContent = Drupal.t('Type');
    // aria-required, NOT the native attribute: a hidden required control
    // silently blocks the host form's submit.
    const select = el(
      'select',
      'ber-tags__filter-select ber-tags__create-type',
    );
    select.setAttribute('aria-required', 'true');
    const placeholder = el('option');
    placeholder.value = '';
    placeholder.textContent = Drupal.t('- Select type -');
    select.appendChild(placeholder);
    options.forEach((option) => {
      const node = el('option');
      node.value = option.bundle;
      node.textContent = option.label;
      select.appendChild(node);
    });
    typeLabel.appendChild(select);

    const titleLabel = el('label', 'ber-tags__create-view-label');
    titleLabel.textContent = Drupal.t('Title');
    const input = el('input', 'ber-tags__create-input');
    input.type = 'text';
    input.maxLength = 255;
    titleLabel.appendChild(input);

    const confirm = iconButton('ber-tags__create-add', Drupal.t('Add'), 'add');
    confirm.disabled = true;
    const cancel = iconButton(
      'ber-tags__create-cancel',
      Drupal.t('Cancel'),
      'close',
    );
    const actions = el('div', 'ber-tags__create-view-actions');
    actions.append(confirm, cancel);

    const error = el('div', 'ber-tags__create-error is-hidden');

    view.append(typeLabel, titleLabel, actions, error);
    wrap.append(toggle, view);

    let pending = false;

    const validate = () => {
      confirm.disabled =
        pending || select.value === '' || input.value.trim() === '';
    };

    const showView = (show) => {
      setHidden(view, !show);
      setHidden(toggle, show);
      setHidden(error, true);
      select.value = '';
      input.value = '';
      validate();
      if (onToggle) {
        onToggle(show);
      }
      if (show) {
        select.focus();
      }
    };

    const collapse = () => {
      showView(false);
      toggle.focus();
    };

    const submit = () => {
      const title = input.value.trim();
      const option = options.find((entry) => entry.bundle === select.value);
      if (!option || title === '' || pending) {
        return;
      }
      pending = true;
      select.disabled = true;
      input.disabled = true;
      validate();
      onSubmit(option, title)
        .then(() => showView(false))
        .catch((submitError) => {
          error.textContent = submitError.message;
          setHidden(error, false);
          announce(submitError.message, 'assertive');
        })
        .finally(() => {
          pending = false;
          select.disabled = false;
          input.disabled = false;
          validate();
          if (!isHidden(view)) {
            input.focus();
          }
        });
    };

    onClick(toggle, () => showView(true));
    onClick(confirm, submit);
    onClick(cancel, collapse);
    select.addEventListener('change', validate);
    input.addEventListener('input', validate);
    view.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target === input) {
        event.preventDefault();
        submit();
      } else if (event.key === 'Escape') {
        // Collapse to the toggle, keep the popover itself open.
        event.preventDefault();
        event.stopPropagation();
        collapse();
      }
    });

    return { el: wrap, close: collapse };
  };
})(Drupal);
